import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2, Download, Star, Loader2, Package, MapPin, Clock, ShieldX, Camera, X, Phone
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  getJobByToken, getSiteSettings, downloadAllPodPhotos, downloadPodPhoto, stopPhotos
} from "../lib/deliveries";

const FALLBACK_REVIEW = "https://www.google.com/search?q=518+Courier+Albany+NY+reviews";

export default function CustomerDeliveryProof() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | disabled | notfound
  const [job, setJob] = useState(null);
  const [stops, setStops] = useState([]);
  const [reviewUrl, setReviewUrl] = useState(FALLBACK_REVIEW);
  const [zipping, setZipping] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [res, settings] = await Promise.all([getJobByToken(token), getSiteSettings().catch(() => ({}))]);
        if (settings?.google_review_url) setReviewUrl(settings.google_review_url);
        if (!res) { setState("notfound"); return; }
        if (res.disabled) { setState("disabled"); return; }
        setJob(res.job); setStops(res.stops); setState("ok");
      } catch (e) {
        console.error("delivery proof load error:", e?.code, e?.message);
        setState("notfound");
      }
    })();
  }, [token]);

  const prettyDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return d; }
  };

  const downloadAll = async () => {
    setZipping(true);
    try { await downloadAllPodPhotos(job, stops); }
    catch (e) { alert(e?.message || "No photos available yet."); }
    finally { setZipping(false); }
  };

  if (state === "loading") return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading your delivery…</div>;

  if (state === "notfound" || state === "disabled") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="delivery-unavailable">
        <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center">
          <ShieldX className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <h1 className="font-display text-xl font-extrabold text-[#0f2847]">Link unavailable</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {state === "disabled"
              ? "This delivery link has expired or been disabled. Please contact 518 Courier for assistance."
              : "We couldn't find a delivery for this link. Please check the link or contact 518 Courier."}
          </p>
          <a href="tel:5189544701" className="inline-flex items-center gap-2 mt-5 text-[#f59321] font-bold"><Phone className="w-4 h-4" /> (518) 954-4701</a>
        </div>
      </div>
    );
  }

  const totalPhotos = stops.reduce((n, s) => n + stopPhotos(s).length, 0);
  const completed = stops.filter((s) => s.status === "delivered").length;
  const total = job.total_stops || stops.length;
  const statusLabel = (job.job_status || "").toLowerCase() === "completed" || (completed >= total && total > 0) ? "COMPLETED" : "IN PROGRESS";

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="customer-delivery-page">
      {/* Header */}
      <section className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-10 max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <img src="/logo192.png" onError={(e) => { e.currentTarget.style.display = "none"; }} alt="518 Courier" className="w-9 h-9 rounded-full" />
            <div className="font-display text-xl font-extrabold">518 <span className="text-[#f59321]">Courier</span></div>
          </div>
          <div className="inline-flex items-center gap-1.5 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-3 py-1 rounded-full text-xs font-bold mb-3">
            <CheckCircle2 className="w-3.5 h-3.5" /> {statusLabel}
          </div>
          <h1 className="font-display text-2xl md:text-4xl font-extrabold leading-tight">Delivery Completed</h1>
          <p className="text-slate-300 mt-2">{job.job_name}{job.client_name ? ` · ${job.client_name}` : ""}</p>
          <p className="text-slate-400 text-sm mt-1">Delivery Date: {prettyDate(job.job_date) || "—"}</p>
          <p className="text-slate-200 mt-5 text-sm md:text-base max-w-xl">
            Your deliveries have been successfully completed. You can view or download the delivery confirmation photos below.
          </p>
        </div>
      </section>

      <div className="container-518 max-w-3xl -mt-6 space-y-6">
        {/* Summary */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="delivery-summary">
          <div><div className="text-2xl font-display font-extrabold text-[#0f2847]">{total}</div><div className="text-xs text-slate-500 font-semibold">Total deliveries</div></div>
          <div><div className="text-2xl font-display font-extrabold text-emerald-600">{completed}</div><div className="text-xs text-slate-500 font-semibold">Completed</div></div>
          <div><div className="text-2xl font-display font-extrabold text-[#0f2847]">{totalPhotos}</div><div className="text-xs text-slate-500 font-semibold">POD photos</div></div>
          <div><div className="text-sm font-display font-extrabold text-[#0f2847] mt-1">{prettyDate(job.job_date) || "—"}</div><div className="text-xs text-slate-500 font-semibold">Date</div></div>
        </div>

        {/* Download all */}
        {totalPhotos > 0 && (
          <Button onClick={downloadAll} disabled={zipping} data-testid="customer-download-all" className="w-full h-14 rounded-2xl bg-[#f59321] hover:bg-[#e07f10] text-white text-base font-extrabold shadow-lg">
            {zipping ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />} DOWNLOAD ALL DELIVERY PHOTOS
          </Button>
        )}

        {/* Gallery by stop */}
        <div className="space-y-4" data-testid="delivery-gallery">
          {stops.map((s) => {
            const photos = stopPhotos(s);
            return (
              <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`customer-stop-${s.stop_number}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {s.package_reference && <span className="text-xs font-bold text-[#f59321]">{s.package_reference}</span>}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.status === "delivered" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                        {s.status === "delivered" ? "DELIVERED" : "PENDING"}
                      </span>
                    </div>
                    <div className="font-bold text-[#0f2847] mt-0.5 truncate">{s.recipient_name}{s.company_name ? ` · ${s.company_name}` : ""}</div>
                    {(s.address_line_1 || s.city) && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {[s.address_line_1, s.city, s.state, s.zip_code].filter(Boolean).join(", ")}</div>}
                    {s.delivered_time && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {new Date(s.delivered_time).toLocaleString()}</div>}
                    {s.recipient_received_by && <div className="text-xs text-slate-500 mt-0.5">Received by: {s.recipient_received_by}</div>}
                  </div>
                </div>
                {photos.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                    {photos.map((p, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden border border-slate-200">
                        <img src={p.url} alt={`POD ${i + 1}`} className="w-full h-24 object-cover cursor-pointer" onClick={() => setViewer(p.url)} data-testid={`customer-photo-${s.stop_number}-${i}`} />
                        <button onClick={() => downloadPodPhoto(s, p.url, i, photos.length)} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Download" data-testid={`customer-download-${s.stop_number}-${i}`}><Download className="w-3.5 h-3.5" /></button>
                        {(p.caption || p.captured_at) && <div className="px-1.5 py-1 text-[9px] text-slate-500 truncate bg-white">{[p.caption, p.captured_at].filter(Boolean).join(" · ")}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> No photos for this stop.</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Review */}
        <div className="bg-[#0f2847] text-white rounded-2xl p-6 text-center" data-testid="review-section">
          <div className="flex justify-center gap-1 mb-3">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-6 h-6 text-[#f59321] fill-[#f59321]" />)}
          </div>
          <h2 className="font-display text-xl font-extrabold">How was your experience with 518 Courier?</h2>
          <p className="text-slate-300 text-sm mt-2 max-w-md mx-auto">Thank you for choosing 518 Courier. If you were satisfied with our service, we would greatly appreciate your feedback.</p>
          <a href={reviewUrl} target="_blank" rel="noreferrer">
            <Button className="mt-5 h-12 px-8 rounded-full bg-[#f59321] hover:bg-[#e07f10] text-white font-extrabold" data-testid="leave-review-btn">
              <Star className="w-4 h-4 mr-2" /> LEAVE US A REVIEW
            </Button>
          </a>
        </div>

        <div className="text-center text-xs text-slate-400 pt-2">
          OBLED Corporation d/b/a 518 Courier · <a href="tel:5189544701" className="font-semibold text-slate-500">(518) 954-4701</a>
        </div>
      </div>

      {/* Lightbox */}
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setViewer(null)} data-testid="customer-lightbox">
          <img src={viewer} alt="POD full" className="max-w-full max-h-full rounded-lg" />
          <button className="absolute top-4 right-4 text-white"><X className="w-7 h-7" /></button>
        </div>
      )}
    </div>
  );
}
